import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { APIUser } from '../models/user.model';
import { JwtService } from '.';
import { Credentials } from '../models/credentials.model';
import { JwtToken } from '../models/jwtToken.model';
import { PasswordData } from '../models/password.model';
import { SignUp } from '../models/signUp.model';

@Injectable()
export class APIUserService {
  private rootUrl = environment.API_URL;

  getheadersNoAuth() {
    let headerDict = {
      'Content-Type': 'application/json',
      'Accept': 'application/json, */*',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': 'true',
      'Access-Token': '',
      'No-Auth': 'True',
      'observe': 'response'
    };
    return headerDict;
  }

  getheadersWithAuth() {
    let headerDict = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Access-Control-Allow-Headers': 'Origin, X-Requested-With, Content-Type, Accept',
      'Access-Control-Allow-Origin': '*',
      'Access-Control-Allow-Credentials': 'true',
      'observe': 'response'
    };
    return headerDict;
  }

  private currentUserSubject = new BehaviorSubject<APIUser>({} as APIUser);
  public currentUser = this.currentUserSubject.asObservable().pipe(distinctUntilChanged());

  private isAuthenticatedSubject = new ReplaySubject<boolean>(1);
  public isAuthenticated = this.isAuthenticatedSubject.asObservable();

  private isAdminSubject = new ReplaySubject<boolean>(1);
  public isAdmin = this.isAdminSubject.asObservable();

  constructor(private http: HttpClient,
    private jwtService: JwtService) { }

  //Unauthenticated user actions
  registerUser(user: SignUp) {
    let body = JSON.stringify(user);
    let reqHeaders = { headers: new HttpHeaders(this.getheadersWithAuth()) };
    return this.http.post(this.rootUrl + '/auth/signup', body, reqHeaders);
  }

  login(credentials: Credentials) {
    let body = JSON.stringify(credentials)
    let reqHeaders = { headers: new HttpHeaders(this.getheadersNoAuth()) };
    return this.http.post(this.rootUrl + '/auth/signin', body, reqHeaders)
      .pipe(map(
        (data: any) => {
          console.log(data)
          let user = new APIUser().deserialize(data.user);
          // Save JWT sent from server in localstorage
          this.jwtService.saveToken(data.token.token);
          // Set isAuthenticated to true
          this.isAuthenticatedSubject.next(true);
          this.currentUserSubject.next(user);
          //Check if user is admin 
          this.isAdminSubject.next(user.role=="Admin")

          return user
        })
      );
  }


  logout() {
    let reqHeaders = { headers: new HttpHeaders(this.getheadersWithAuth()) };
    return this.http.get(this.rootUrl + '/auth/logout', reqHeaders)
      .pipe(map(
        (data: any) => {
          this.purgeAuth();
        }));
  }

  getUser() {
    let reqHeaders = { headers: new HttpHeaders(this.getheadersWithAuth()) };
    return this.http.get(this.rootUrl + '/auth/user', reqHeaders)
      .pipe(map(
        (data: any) => {
          let user = new APIUser().deserialize(data);
          this.currentUserSubject.next(user);
          //Check if user is admin 
          this.isAdminSubject.next(user.role=="Admin")
          return user
        }));
  }

  changePassword(password: PasswordData) {
    let body = JSON.stringify(password);
    let reqHeaders = { headers: new HttpHeaders(this.getheadersWithAuth()) };
    return this.http.post(this.rootUrl + '/auth/change-password', body, reqHeaders);
  }


  changeUserPassword(password: PasswordData) {
    let body = JSON.stringify(password);
    let reqHeaders = { headers: new HttpHeaders(this.getheadersWithAuth()) };
    return this.http.post(this.rootUrl + '/auth/change-user-password', body, reqHeaders);
  }

  //Utility methods

  // Verify JWT in localstorage with server & load user's info.
  // This runs once on application startup.
  populate() {
    // If JWT detected, attempt to get & store user's info
    let token = this.jwtService.getToken();
    if (token) {
      this.getUser()
        .subscribe(
          (res: APIUser) => {
            console.log("got user at startup")
            this.setAuth(res, token)
          },
          err => this.purgeAuth()
        );
    } else {
      // Remove any potential remnants of previous auth states
      console.log("in else clause")
      this.purgeAuth();
    }
  }

  private setAuth(user: APIUser, token: String) {
    // Save JWT sent from server in localstorage
    // this.jwtService.saveToken(token);
    // Set current user data into observable
    this.currentUserSubject.next(user);
    // Set isAuthenticated to true
    this.isAuthenticatedSubject.next(true);
    //Check if user is admin 
    this.isAdminSubject.next(user.role=="Admin")
  }

  purgeAuth() {
    console.log("in purge auth")
    // Remove JWT from localstorage
    this.jwtService.destroyToken();
    // Set current user to an empty object
    this.currentUserSubject.next({} as APIUser);
    // Set auth status to false
    this.isAuthenticatedSubject.next(false);
    //Set Admin status to false
    this.isAdminSubject.next(false)
  }

  getCurrentUser(): APIUser {
    return this.currentUserSubject.value;
  }
}
